//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useState, useEffect, useContext } from 'react';
import { useParams   } from 'react-router';
import { useNavigate } from 'react-router-dom';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import {
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { Add, Cancel, Save, ToggleOn, ToggleOff } from '@mui/icons-material';

// === COMPONENTS ===
import { Spacer            } from 'components/Spacer.jsx';
import { ExpressionContext } from '../ExpressionContext/ExpressionContext';
import { DocumentSelect    } from '../ExpressionView/components/Expression/components/DocumentSelect/DocumentSelect.jsx';

// === API ===
import { on, removeListener, sendMessage } from 'api/prokey/prokey.websocket';

// === UTILS ===
import { getHexAlpha } from 'components/styles.utils';

//#endregion

//-----------------------------------------------------------------
export function InfoView() {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { rule_id } = useParams();
  const navigate = useNavigate();

  const { expressions, predicates, save, is_saved } = useContext( ExpressionContext );

  const [ rule       , setRule        ] = useState( {} );
  const [ description, setDescription ] = useState( '' );
  const [ tags       , setTags        ] = useState( {} );
  const [ is_active  , setIsActive    ] = useState( false );
  const [ is_required_document , setIsRequiredDocument ] = useState( false );

  const [ is_loaded, setIsLoaded ] = useState( false );

  //#endregion

  //#region [STYLES]

  const theme = useTheme();
  const _styles = {
    container: {
      gridColumn   : 1        ,
      gridRow      : 1        ,
      width        : '70rem'  ,
      overflow     : 'hidden' ,
      padding      : '1rem'   ,
      borderRadius : '1rem'   ,
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    container_exp: {
      height       : '100%'   ,
      width        : '100%'   ,
      overflow     : 'hidden' ,
      borderRadius : '1rem'   ,
    },
    container_exp_2: {
      width    : '100%' ,
      height   : 'calc( 100% - 4rem )' ,
      overflow : 'auto' ,
      backgroundColor: theme.palette.background.secondary[theme.palette.mode],
    },
    header: {
      display: 'flex',
      fontSize: '2rem',
    },
    border_1: {
      display: 'flex',
      borderRadius: '1rem',
      width: '100%',
    },
    button_save: {
      width  : '3rem',
      height : '3rem',
      backgroundColor: theme.palette.primary[theme.palette.mode],
      color: 'white',
      '&:hover': {
        // backgroundColor: 'white',
        // color: theme.palette.primary[theme.palette.mode],
        backgroundColor: theme.palette.primary[theme.palette.mode] + getHexAlpha(50),
        color: 'white',
      }
    },
    button_toggle: {
      width  : '3rem',
      height : '3rem',
      color: is_active ? 'green' : 'red',
      fontSize: 40,
    },
    button_cancel: {
      width  : '3rem',
      height : '3rem',
      backgroundColor: theme.palette.error.main,
      color: 'white',
    },
  }

  const CONTAINER = {
    style: _styles.container,
  };
  const HEADER = {
    style: _styles.header,
  };
  const BUTTON_SAVE = {
    sx: _styles.button_save,
    children: <Save />,
    onClick: onClick_Save,
    name: 'da_save_rule_btn', 
  };
  const BUTTON_TOGGLE = {
    sx: _styles.button_toggle,
    onClick: onClick_Toggle,
    name: 'da_toggle_rule_active_btn',
  };
  const BUTTON_CANCEL = {
    sx: _styles.button_cancel,
    children: <Cancel />,
    onClick: onClick_Cancel,
    name: 'da_close_info_view_btn',
  };
  const TEXT_DESCRIPTION = {
    sx: {width: '100%'},
    defaultValue: description,
    multiline : true,
    onChange : (event) => setDescription( event.target.value ),
    name: 'da_rule_description_input',
  };
    
  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    //-----------------------------------------------------------------    
    function listener(data) {
      //-----------------------------------------------------------------
      setRule       ( data.rule               || {} );
      setDescription( data.rule.description   || '' );
      setTags       ( data.rule.tags          || {} );
      setIsActive   ( data.rule.is_active     || false );

      setIsLoaded( true );
    }
    function listenerRuleCreateResonse( data ) {
      navigate( '/analytics/rules/' + data.id , { replace: true })
    }

    on( 'ANALYTICS_RULE' , listener );
    on( 'RULE_CREATE_RESPONSE', listenerRuleCreateResonse )
    
    const message = JSON.stringify( { type: 'ANALYTICS_RULE_REQUEST', data: { id: rule_id } } );
    sendMessage( message );

    return () => {
      removeListener( 'ANALYTICS_RULE' , listener );
      removeListener( 'RULE_CREATE_RESPONSE' , listenerRuleCreateResonse );
    };
  }, [] ); // empty dependency = called only once at startup

  //#endregion

  //#region [MODIFY]

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onClick_Save() {
    //-----------------------------------------------------------------
    const tags_new = { ...tags, is_required_document };

    const update = {
      id          : rule_id     ,
      is_active   : is_active ? 1 : 0,
      description : description ,
      tags        : tags_new    ,
      predicates  : predicates .map( x => x.data ) ,
      expressions : expressions.map( x => x.data ) ,
    }
    
    const type = rule_id === 'new' ? 'RULE_CREATE' : 'RULE_UPDATE';
    const message = JSON.stringify( { type, data: { rule: update } } );
    sendMessage( message );

    save();
  }

  //-----------------------------------------------------------------
  function onClick_Toggle() {
    //-----------------------------------------------------------------
    setIsActive( !is_active );
  }

  //-----------------------------------------------------------------
  function onClick_Cancel() {
    //-----------------------------------------------------------------
    if( is_saved || window.confirm('Data is not saved. Leave the page?') ) {
      navigate( '/analytics/rules', { replace: true } );
    }
  }
  
  //-----------------------------------------------------------------
  function onChange_Document( value ) {
    //-----------------------------------------------------------------
    setTags( {...tags, document: value.numcode } )
  }

  //#endregion

  //#region [DISPLAY]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    const active_icon = is_active ? <ToggleOn { ...BUTTON_TOGGLE } /> : <ToggleOff { ...BUTTON_TOGGLE } />;
    return (
      <div { ...CONTAINER } >
        <div { ...HEADER }>
          Description:
          <Spacer fill />
          <Tooltip title='Close' placement='top'>
            <IconButton { ...BUTTON_CANCEL } />
          </Tooltip>
          <Spacer />
          <Tooltip title='Toggle active' placement='top'>
            {active_icon}
          </Tooltip>
          <Spacer />
          <Tooltip title='Save rule' placement='top'>
            <IconButton { ...BUTTON_SAVE } />
          </Tooltip>
        </div>
        <Spacer />
        <TextField { ...TEXT_DESCRIPTION } />
        <Spacer height='2rem' />
        <div { ...HEADER }>
          Document:
          <Spacer height='2rem' />
          <DocumentSelect onChange={onChange_Document} numcode={tags.document} />
        </div>
        <Spacer height='2rem' />
      </div>
    );
  }

  //#endregion

  return display();
}
